import React, { useEffect, useState } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { auth } from '../../../../firebase/config';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiCheck, FiX, FiEdit, FiTrash2, FiFilter, FiAlertTriangle, FiCpu, FiLoader, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import Icon from '../../../../components/Icon';
import AddDeploymentRequestModal from '../components/AddDeploymentRequestModal/index';
import EditDeploymentRequestModal from '../components/EditDeploymentRequestModal/index';
import Modal from '../../../../components/Modal';
import Table, { Column } from '../../../../components/Table';
import TextField from '../../../../components/TextField';
import {
    fetchDeploymentRequestsData,
    updateDeploymentRequestStatus,
    deleteDeploymentRequest,
    fetchDeploymentRequestBankId,
    calculateRegionalVendorShare,
    updateInchargeReadStatus,
    type DeploymentRequest
} from '../service/deploymentRequestService';
import { designationIsIncharge } from '../../../../utils/designationRole';
import { useAppSelector } from '../../../../store';

type DRStatusFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'cancelled' | 'forwarded_to_head';
type ShareStats = { allocatedCount: number; totalBranches: number; percentage: number } | null;

function startOfLocalDayYmd(ymd: string): Date {
    const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function endOfLocalDayYmd(ymd: string): Date {
    const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d, 23, 59, 59, 999);
}

function getRequestDate(req: DeploymentRequest): Date | null {
    const rd = req.createdAt as any;
    if (!rd) return null;
    if (typeof rd.toDate === 'function') return rd.toDate() as Date;
    try {
        return new Date(rd);
    } catch {
        return null;
    }
}

export default function DeploymentRequests() {
    const [requests, setRequests] = useState<DeploymentRequest[]>([]);
    const [filtered, setFiltered] = useState<DeploymentRequest[]>([]);
    
    // Search states
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    
    // Filter states
    const [statusFilter, setStatusFilter] = useState<DRStatusFilter>('all');
    const [dateFilter, setDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    const [dateFrom, setDateFrom] = useState<string | null>(null);
    const [dateTo, setDateTo] = useState<string | null>(null);

    // Draft filters for modal
    const [draftStatusFilter, setDraftStatusFilter] = useState<DRStatusFilter>('all');
    const [draftDateFilter, setDraftDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    const [draftDateFrom, setDraftDateFrom] = useState<string | null>(null);
    const [draftDateTo, setDraftDateTo] = useState<string | null>(null);
    
    // Modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    
    // Review and Detail state
    const [selectedRequest, setSelectedRequest] = useState<DeploymentRequest | null>(null);
    const [requestToDelete, setRequestToDelete] = useState<DeploymentRequest | null>(null);
    const [rejectingRequest, setRejectingRequest] = useState<DeploymentRequest | null>(null);
    
    const [regionalRemarksInput, setRegionalRemarksInput] = useState('');
    const [headRemarksInput, setHeadRemarksInput] = useState('');
    const [rejectReason, setRejectReason] = useState('');
    const [rejectError, setRejectError] = useState('');
    
    // Live shares for detail view
    const [detailCctvShare, setDetailCctvShare] = useState<ShareStats>(null);
    const [detailBamsShare, setDetailBamsShare] = useState<ShareStats>(null);
    const [detailGuardShare, setDetailGuardShare] = useState<ShareStats>(null);
    const [loadingDetailShares, setLoadingDetailShares] = useState(false);
    
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [updatingReadStatus, setUpdatingReadStatus] = useState<string | null>(null);
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });

    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const pso = useAppSelector((s) => s.user.pso);

    const psoDesignation =
        (pso?.designation as string | undefined)?.trim() ||
        (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('psoDesignation')?.trim() || undefined : undefined);

    const isRegionalHead = !!pso && /regional/i.test(pso.designation || '');
    const isHead = psoDesignation === 'Head';
    const isIncharge = designationIsIncharge(psoDesignation);
    const inchargeType = psoDesignation?.replace(/_/g, ' ').toLowerCase() || '';
    const isInchargeCCTV = isIncharge && inchargeType.includes('cctv');
    const isInchargeBAMS = isIncharge && inchargeType.includes('bams');
    const isInchargeGuarding = isIncharge && inchargeType.includes('guard');
    
    const canCreateRequest = !isBankUser && !isIncharge && !isRegionalHead && !isHead;
    const isPSO = !isBankUser && !isIncharge && !isRegionalHead && !isHead;

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    // Load Data
    const loadRequests = async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const data = await fetchDeploymentRequestsData(uid, isBankUser, isRegionalHead, psoDesignation, isHead);
            setRequests(data);
            setFiltered(data);
        } catch (err) {
            console.error(err);
            showToast('Failed to load deployment requests', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRequests();
    }, [uid, isBankUser, isRegionalHead, psoDesignation, isHead]);

    // Setup default filter on mount based on role
    useEffect(() => {
        const defaultStatus = isRegionalHead ? 'pending' : isHead ? 'forwarded_to_head' : 'all';
        setStatusFilter(defaultStatus);
        setDraftStatusFilter(defaultStatus);
    }, [isRegionalHead, isHead]);

    // Apply Search and Filters
    useEffect(() => {
        let list = [...requests];

        // Apply Status Filter
        if (statusFilter !== 'all') {
            list = list.filter(r => {
                const s = (r.status || 'Pending').toLowerCase().trim();
                if (statusFilter === 'pending') return s === 'pending';
                if (statusFilter === 'approved') return s === 'approved';
                if (statusFilter === 'rejected') return s === 'rejected';
                if (statusFilter === 'cancelled') return s === 'cancelled';
                if (statusFilter === 'forwarded_to_head') return s === 'forwarded to head';
                return true;
            });
        }

        // Apply Date range or Quick period
        if (dateFrom && dateTo) {
            const fromMs = startOfLocalDayYmd(dateFrom).getTime();
            const toMs = endOfLocalDayYmd(dateTo).getTime();
            list = list.filter(r => {
                const d = getRequestDate(r);
                if (!d) return false;
                const t = d.getTime();
                return t >= fromMs && t <= toMs;
            });
        } else if (dateFilter !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            list = list.filter(r => {
                const requestDate = getRequestDate(r);
                if (!requestDate) return false;
                const requestDateOnly = new Date(
                    requestDate.getFullYear(),
                    requestDate.getMonth(),
                    requestDate.getDate()
                );
                switch (dateFilter) {
                    case 'daily':
                        return requestDateOnly.getTime() === today.getTime();
                    case 'weekly': {
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        return requestDateOnly >= weekAgo && requestDateOnly <= today;
                    }
                    case 'monthly': {
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(today.getMonth() - 1);
                        return requestDateOnly >= monthAgo && requestDateOnly <= today;
                    }
                    default:
                        return true;
                }
            });
        }

        // Apply Search Term
        if (appliedSearchTerm) {
            const term = appliedSearchTerm.toLowerCase();
            list = list.filter(r => {
                const reqId = (r.requestId || '').toLowerCase();
                const bCode = (r.branch?.branchCode || '').toLowerCase();
                const bAddress = (r.branch?.address || '').toLowerCase();
                const cctv = (r.cctvCompany?.companyName || '').toLowerCase();
                const guard = (r.guardCompany?.companyName || '').toLowerCase();
                const bams = (r.bamsCompany?.companyName || '').toLowerCase();

                return reqId.includes(term) ||
                    bCode.includes(term) ||
                    bAddress.includes(term) ||
                    cctv.includes(term) ||
                    guard.includes(term) ||
                    bams.includes(term);
            });
        }

        setFiltered(list);
        setCurrentPage(1);
    }, [requests, statusFilter, dateFilter, dateFrom, dateTo, appliedSearchTerm]);

    // Fetch share percentages for detail modal
    useEffect(() => {
        if (selectedRequest && selectedRequest.branch?.locationId && uid) {
            const loadStats = async () => {
                setLoadingDetailShares(true);
                try {
                    const bankId = await fetchDeploymentRequestBankId(uid, isBankUser);
                    if (bankId) {
                        const locId = selectedRequest.branch!.locationId!;
                        
                        const cctvS = selectedRequest.cctvCompanyId 
                            ? await calculateRegionalVendorShare(bankId, locId, selectedRequest.cctvCompanyId) 
                            : null;
                        const bamsS = selectedRequest.bamsCompanyId 
                            ? await calculateRegionalVendorShare(bankId, locId, selectedRequest.bamsCompanyId) 
                            : null;
                        const guardS = selectedRequest.guardCompanyId 
                            ? await calculateRegionalVendorShare(bankId, locId, selectedRequest.guardCompanyId) 
                            : null;
                            
                        setDetailCctvShare(cctvS);
                        setDetailBamsShare(bamsS);
                        setDetailGuardShare(guardS);
                    }
                } catch (error) {
                    console.error("Error loading detail share metrics:", error);
                } finally {
                    setLoadingDetailShares(false);
                }
            };
            loadStats();
        } else {
            setDetailCctvShare(null);
            setDetailBamsShare(null);
            setDetailGuardShare(null);
        }
    }, [selectedRequest, uid]);

    // Search and Filter Handlers
    const applySearch = () => {
        setAppliedSearchTerm(searchInput.trim());
    };

    const openFilterModal = () => {
        setDraftStatusFilter(statusFilter);
        setDraftDateFilter(dateFilter);
        setDraftDateFrom(dateFrom);
        setDraftDateTo(dateTo);
        setShowFilterModal(true);
    };

    const applyFiltersModal = () => {
        setStatusFilter(draftStatusFilter);
        setDateFilter(draftDateFilter);
        setDateFrom(draftDateFrom);
        setDateTo(draftDateTo);
        setShowFilterModal(false);
    };

    const clearFilters = () => {
        const defaultStatus = isRegionalHead ? 'pending' : isHead ? 'forwarded_to_head' : 'all';
        setStatusFilter(defaultStatus);
        setDateFilter('all');
        setDateFrom(null);
        setDateTo(null);
        
        setDraftStatusFilter(defaultStatus);
        setDraftDateFilter('all');
        setDraftDateFrom(null);
        setDraftDateTo(null);
    };

    const hasClearableFilters =
        statusFilter !== (isRegionalHead ? 'pending' : isHead ? 'forwarded_to_head' : 'all') ||
        dateFilter !== 'all' ||
        !!(dateFrom && dateTo);

    // AI Remarks Analyzer
    const aiRemarksContent = () => {
        if (!selectedRequest?.branch) return '';
        const region = selectedRequest.branch.locationName || 'the region';
        const alerts: string[] = [];
        
        if (detailCctvShare && detailCctvShare.percentage > 50) {
            alerts.push(`⚠️ Selected CCTV Vendor holds a dominant share of ${detailCctvShare.percentage}% in ${region}.`);
        }
        if (detailBamsShare && detailBamsShare.percentage > 50) {
            alerts.push(`⚠️ Selected BAMS Vendor holds a dominant share of ${detailBamsShare.percentage}% in ${region}.`);
        }
        if (detailGuardShare && detailGuardShare.percentage > 50) {
            alerts.push(`⚠️ Selected Guarding Vendor holds a dominant share of ${detailGuardShare.percentage}% in ${region}.`);
        }

        if (alerts.length === 0) {
            return `✅ AI Suggestion: All selected vendors maintain a balanced distribution (under 50% share each) in ${region}. Recommending final approval to maintain healthy vendor diversity.`;
        } else {
            return `${alerts.join(' ')} AI Suggestion: Dominant regional concentration detected. Suggest review or alternate selection before final approval.`;
        }
    };

    // Actions
    const handleForwardToHead = async () => {
        if (!selectedRequest || !uid || !selectedRequest.bankId) return;
        setLoading(true);
        setActionLoading(true);
        try {
            const forwardedByName = sessionStorage.getItem('name') || auth.currentUser?.displayName || 'Regional PSO';
            await updateDeploymentRequestStatus(selectedRequest.bankId, selectedRequest.id, 'Forwarded to Head', {
                forwardedBy: uid,
                forwardedByName,
                regionalHeadRemarks: regionalRemarksInput.trim()
            });
            showToast('✅ Allocation request forwarded to Head!', 'success');
            setShowViewModal(false);
            setSelectedRequest(null);
            await loadRequests();
        } catch (err) {
            console.error(err);
            showToast('Failed to forward request', 'error');
        } finally {
            setLoading(false);
            setActionLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!selectedRequest || !uid || !selectedRequest.bankId) return;
        setLoading(true);
        setActionLoading(true);
        try {
            const approvedByName = sessionStorage.getItem('name') || auth.currentUser?.displayName || 'Head Reviewer';
            await updateDeploymentRequestStatus(selectedRequest.bankId, selectedRequest.id, 'Approved', {
                approvedBy: uid,
                approvedByName,
                headSecurityRemarks: headRemarksInput.trim(),
                branchId: selectedRequest.branchId,
                cctvCompanyId: selectedRequest.cctvCompanyId,
                bamsCompanyId: selectedRequest.bamsCompanyId,
                guardCompanyId: selectedRequest.guardCompanyId,
                psoId: selectedRequest.psoId || uid
            });
            showToast('✅ Allocation request approved & deployments completed!', 'success');
            setShowViewModal(false);
            setSelectedRequest(null);
            await loadRequests();
        } catch (err) {
            console.error(err);
            showToast('Failed to approve request', 'error');
        } finally {
            setLoading(false);
            setActionLoading(false);
        }
    };

    const handleCancel = async (req: DeploymentRequest) => {
        if (!uid || !req.bankId) return;
        if (!window.confirm(`Are you sure you want to cancel request ${req.requestId}?`)) return;

        setLoading(true);
        setActionLoading(true);
        try {
            await updateDeploymentRequestStatus(req.bankId, req.id, 'Cancelled');
            showToast('✅ Deployment Request cancelled', 'info');
            setShowViewModal(false);
            setSelectedRequest(null);
            await loadRequests();
        } catch (err) {
            console.error(err);
            showToast('Failed to cancel request', 'error');
        } finally {
            setLoading(false);
            setActionLoading(false);
        }
    };

    const handleSetReadStatus = async (req: DeploymentRequest, newReadStatus: boolean) => {
        if (!req.bankId || !req.id) return;
        setUpdatingReadStatus(req.id);
        try {
            await updateInchargeReadStatus(req.bankId, req.id, newReadStatus);
            setRequests(prev => prev.map(item => item.id === req.id ? { ...item, inchargeRead: newReadStatus } : item));
            setFiltered(prev => prev.map(item => item.id === req.id ? { ...item, inchargeRead: newReadStatus } : item));
            showToast(`Request marked as ${newReadStatus ? 'Read' : 'Unread'}`, 'success');
        } catch (error: any) {
            console.error('Error updating read status:', error);
            showToast(`Failed to update status: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            setUpdatingReadStatus(null);
        }
    };

    const handleRejectClick = () => {
        setRejectingRequest(selectedRequest);
        setRejectReason('');
        setRejectError('');
        setShowRejectModal(true);
    };

    const handleConfirmReject = async () => {
        if (!rejectingRequest || !uid || !rejectingRequest.bankId) return;
        if (!rejectReason.trim()) {
            setRejectError('Rejection reason is mandatory.');
            return;
        }

        setLoading(true);
        setActionLoading(true);
        try {
            const rejectedByName = sessionStorage.getItem('name') || auth.currentUser?.displayName || 'Reviewer';
            const rejectionStage = isRegionalHead ? 'Regional' : 'Head';

            const payload: any = {
                rejectReason: rejectReason.trim(),
                rejectedBy: uid,
                rejectedByName,
                rejectionStage
            };

            if (isHead) {
                payload.headSecurityRemarks = rejectReason.trim();
            } else if (isRegionalHead) {
                payload.regionalHeadRemarks = rejectReason.trim();
            }

            await updateDeploymentRequestStatus(rejectingRequest.bankId, rejectingRequest.id, 'Rejected', payload);

            showToast('✅ Deployment Request rejected', 'info');
            setShowRejectModal(false);
            setRejectingRequest(null);
            setRejectReason('');
            setShowViewModal(false);
            setSelectedRequest(null);
            await loadRequests();
        } catch (err) {
            console.error(err);
            showToast('Failed to reject request', 'error');
        } finally {
            setLoading(false);
            setActionLoading(false);
        }
    };

    const handleDeleteClick = (req: DeploymentRequest) => {
        setRequestToDelete(req);
    };

    const handleConfirmDelete = async () => {
        if (!requestToDelete || !uid || !requestToDelete.bankId) return;
        setLoading(true);
        setActionLoading(true);
        try {
            await deleteDeploymentRequest(requestToDelete.bankId, requestToDelete.id);
            showToast('✅ Deployment Request deleted successfully!', 'success');
            setRequestToDelete(null);
            await loadRequests();
        } catch (err) {
            console.error(err);
            showToast('Failed to delete request', 'error');
        } finally {
            setLoading(false);
            setActionLoading(false);
        }
    };

    const openViewModal = (req: DeploymentRequest) => {
        setSelectedRequest(req);
        setRegionalRemarksInput(req.regionalHeadRemarks || '');
        setHeadRemarksInput(req.headSecurityRemarks || '');
        setShowViewModal(true);
    };

    const openEditModal = (req: DeploymentRequest) => {
        setSelectedRequest(req);
        setShowEditModal(true);
    };

    // Columns configuration
    const columns: Column<DeploymentRequest>[] = [
        {
            header: 'Request ID',
            width: '10%',
            render: (r) => (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {r.requestId || 'N/A'}
                </span>
            )
        },
        {
            header: 'Branch Code',
            width: '8%',
            render: (r) => (
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {r.branch?.branchCode || 'N/A'}
                </span>
            )
        },
        {
            header: 'Request Date',
            width: '10%',
            render: (r) => (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    {r.requestDate || 'N/A'}
                </span>
            )
        },
        {
            header: 'Covered Area',
            width: '10%',
            render: (r) => (
                <span className="text-xs text-gray-600 dark:text-gray-400">
                    {r.coveredArea ? `${r.coveredArea} sq ft` : 'N/A'}
                </span>
            )
        },
        {
            header: 'Requested Vendors',
            width: '25%',
            render: (r) => {
                const parts: string[] = [];
                if ((!isIncharge || isInchargeCCTV) && r.cctvCompany) parts.push(`CCTV: ${r.cctvCompany.companyName}`);
                if ((!isIncharge || isInchargeBAMS) && r.bamsCompany) parts.push(`BAMS: ${r.bamsCompany.companyName}`);
                if ((!isIncharge || isInchargeGuarding) && r.guardCompany) parts.push(`Guard: ${r.guardCompany.companyName}`);
                return (
                    <div className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                        {parts.map((p, i) => <div key={i} className="truncate max-w-[250px]">{p}</div>)}
                    </div>
                );
            }
        },
        {
            header: 'Status',
            width: '12%',
            render: (r) => {
                const s = r.status || 'Pending';
                let colors = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
                if (s === 'Approved') colors = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
                if (s === 'Rejected') colors = 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
                if (s === 'Cancelled') colors = 'bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-400';
                if (s === 'Forwarded to Head') colors = 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';

                return (
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${colors}`}>
                        {s}
                    </span>
                );
            }
        },
        ...(isIncharge ? [{
            header: 'Read Status',
            width: '12%',
            render: (r: DeploymentRequest) => {
                const isRead = !!r.inchargeRead;
                return (
                    <select
                        value={isRead ? 'read' : 'unread'}
                        onChange={(e) => {
                            const val = e.target.value;
                            void handleSetReadStatus(r, val === 'read');
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={updatingReadStatus === r.id}
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold border border-transparent cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 transition-all duration-200 ${
                            isRead 
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800 focus:ring-emerald-500' 
                            : 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800 focus:ring-rose-500 font-bold'
                        }`}
                    >
                        <option value="unread" className="bg-white text-rose-850 dark:bg-gray-800 dark:text-rose-350">Unread</option>
                        <option value="read" className="bg-white text-emerald-850 dark:bg-gray-800 dark:text-emerald-355">Read</option>
                    </select>
                );
            }
        }] : []),
        {
            header: 'Actions',
            width: '11%',
            render: (r) => (
                <div className="flex gap-2">
                    <button
                        onClick={() => openViewModal(r)}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-750"
                        title="View details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    {isPSO && r.status === 'Pending' && (
                        <button
                            onClick={() => handleCancel(r)}
                            className="inline-flex items-center justify-center rounded-lg border border-yellow-200 bg-white p-2 text-xs font-semibold text-yellow-600 hover:bg-yellow-50 dark:border-yellow-900/20 dark:bg-gray-800 dark:text-yellow-400 dark:hover:bg-yellow-950/20"
                            title="Cancel request"
                        >
                            <Icon icon={FiX} className="h-4 w-4" />
                        </button>
                    )}
                    {isPSO && (r.status === 'Cancelled' || r.status === 'Rejected') && (
                        <button
                            onClick={() => handleDeleteClick(r)}
                            className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-white p-2 text-xs font-semibold text-red-600 hover:bg-red-50 dark:border-red-900/30 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-950/20"
                            title="Delete request"
                        >
                            <Icon icon={FiTrash2} className="h-4 w-4" />
                        </button>
                    )}
                    {isBankUser && (
                        <>
                            <button
                                onClick={() => openEditModal(r)}
                                className="inline-flex items-center justify-center rounded-lg border border-blue-200 bg-white p-2 text-xs font-semibold text-blue-600 hover:bg-blue-50 dark:border-blue-900/30 dark:bg-gray-800 dark:text-blue-400 dark:hover:bg-blue-950/20"
                                title="Edit request"
                            >
                                <Icon icon={FiEdit} className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => handleDeleteClick(r)}
                                className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-white p-2 text-xs font-semibold text-red-600 hover:bg-red-50 dark:border-red-900/30 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-950/20"
                                title="Delete request"
                            >
                                <Icon icon={FiTrash2} className="h-4 w-4" />
                            </button>
                        </>
                    )}
                </div>
            )
        }
    ];

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Search and Filter Bar Layout
    const filtersLayout = (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex w-full flex-1 items-stretch gap-3 md:pr-3">
                    <div className="min-w-0 flex-1">
                        <TextField
                            type="text"
                            className="!h-11"
                            placeholder="Search by request ID, branch code, or vendors..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    applySearch();
                                }
                            }}
                            prefix={<Icon icon={FiSearch} />}
                            suffix={
                                searchInput ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearchInput('');
                                            setAppliedSearchTerm('');
                                            setCurrentPage(1);
                                        }}
                                        className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                        aria-label="Clear search"
                                        title="Clear search"
                                    >
                                        <Icon icon={FiX} className="h-4 w-4" />
                                    </button>
                                ) : null
                            }
                        />
                    </div>
                    <Button onClick={applySearch} className="!h-11 !py-0 flex shrink-0 items-center gap-2">
                        <Icon icon={FiSearch} className="h-4 w-4" />
                        Search
                    </Button>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-3">
                    <Button variant="secondary" onClick={openFilterModal} className="flex items-center gap-2">
                        <Icon icon={FiFilter} className="h-4 w-4" />
                        Filters
                    </Button>
                    {hasClearableFilters && (
                        <Button variant="secondary" onClick={clearFilters}>
                            Clear Filters
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className="space-y-6">
                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(r) => r.id}
                    loading={loading}
                    emptyMessage="No vendor allocation requests found."
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                    title="Vendor Allocation Requests"
                    description="Allocate security and BAMS vendors to newly renovated branches"
                    headerActions={
                        canCreateRequest ? (
                            <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
                                <Icon icon={FiPlus} className="h-4 w-4" />
                                Create Request
                            </Button>
                        ) : undefined
                    }
                    filters={filtersLayout}
                />
            </div>

            {/* Modals */}
            <AddDeploymentRequestModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={loadRequests}
            />

            <EditDeploymentRequestModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedRequest(null);
                }}
                onSuccess={loadRequests}
                request={selectedRequest}
            />

            {/* Filters Modal */}
            <Modal
                isOpen={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                title="Filter Requests"
                size="lg"
            >
                <div className="space-y-4 p-6">
                    {/* Status */}
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Filter by Status
                        </label>
                        <select
                            value={draftStatusFilter}
                            onChange={(e) => setDraftStatusFilter(e.target.value as DRStatusFilter)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="all">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="forwarded_to_head">Forwarded to Head</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>

                    {/* Quick period */}
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Quick Period
                        </label>
                        <select
                            value={draftDateFilter}
                            onChange={(e) => {
                                const v = e.target.value as 'all' | 'daily' | 'weekly' | 'monthly';
                                setDraftDateFilter(v);
                                setDraftDateFrom(null);
                                setDraftDateTo(null);
                            }}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="all">All Time</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                    </div>

                    {/* Date range */}
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Date Range
                        </label>
                        <DatePicker.RangePicker
                            className="w-full [&_.ant-picker]:min-h-[48px] [&_.ant-picker]:w-full [&_.ant-picker-input>input]:text-sm"
                            value={
                                draftDateFrom && draftDateTo
                                    ? [dayjs(draftDateFrom), dayjs(draftDateTo)]
                                    : null
                            }
                            allowClear
                            format="MMM D, YYYY"
                            onChange={(dates) => {
                                if (!dates || !dates[0] || !dates[1]) {
                                    setDraftDateFrom(null);
                                    setDraftDateTo(null);
                                    return;
                                }
                                setDraftDateFrom(dates[0].format('YYYY-MM-DD'));
                                setDraftDateTo(dates[1].format('YYYY-MM-DD'));
                                setDraftDateFilter('all');
                            }}
                        />
                    </div>

                    {/* Footer Actions */}
                    <div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                        <Button variant="secondary" type="button" onClick={() => setShowFilterModal(false)}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={applyFiltersModal}>
                            Apply
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Rejection Reason Modal */}
            <Modal
                isOpen={showRejectModal}
                onClose={() => {
                    setShowRejectModal(false);
                    setRejectingRequest(null);
                }}
                title="Reject Allocation Request"
                size="sm"
            >
                <div className="space-y-4 p-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Reason for Rejection *
                        </label>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => {
                                setRejectReason(e.target.value);
                                setRejectError('');
                            }}
                            placeholder="Please provide comments for rejecting this allocation request..."
                            rows={3}
                            className="w-full rounded-xl border border-gray-300 p-2.5 text-sm outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/10 dark:border-gray-600 dark:bg-black/30"
                        />
                        {rejectError && <p className="text-red-500 text-xs mt-1">{rejectError}</p>}
                    </div>
                    <div className="flex justify-end gap-3 border-t pt-4">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowRejectModal(false);
                                setRejectingRequest(null);
                            }}
                            disabled={actionLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmReject}
                            disabled={actionLoading}
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white flex items-center gap-1.5"
                        >
                            {actionLoading && <Icon icon={FiLoader} className="h-4 w-4 animate-spin" />}
                            {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Confirm Delete Modal */}
            <Modal
                isOpen={!!requestToDelete}
                onClose={() => setRequestToDelete(null)}
                title="Delete Allocation Request"
                size="sm"
            >
                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Are you sure you want to permanently delete allocation request{' '}
                        <strong className="text-gray-900 dark:text-white">
                            {requestToDelete?.requestId || 'N/A'}
                        </strong>?
                    </p>
                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button variant="secondary" onClick={() => setRequestToDelete(null)} disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleConfirmDelete}
                            disabled={actionLoading}
                            className="flex items-center gap-1.5"
                        >
                            {actionLoading && <Icon icon={FiLoader} className="h-4 w-4 animate-spin" />}
                            {actionLoading ? 'Deleting...' : 'Delete'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* View Details Modal (highly comprehensive with share stats and AI suggestion) */}
            <Modal
                isOpen={showViewModal}
                onClose={() => {
                    setShowViewModal(false);
                    setSelectedRequest(null);
                }}
                title={`Allocation Request Details (${selectedRequest?.requestId})`}
                size="lg"
            >
                <div className="space-y-6 px-6 pb-6 max-h-[75vh] overflow-y-auto text-sm">
                    {/* Branch Details */}
                    <div className="bg-gray-50 dark:bg-gray-800/30 p-4 rounded-xl border space-y-3">
                        <h4 className="font-bold text-gray-800 dark:text-gray-200">Renovated Branch Details</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                            <div>
                                <span className="text-gray-500 block">Branch Code:</span> 
                                <strong className="text-gray-900 dark:text-gray-100">{selectedRequest?.branch?.branchCode || 'N/A'}</strong>
                            </div>
                            <div>
                                <span className="text-gray-500 block">Branch Location:</span> 
                                <strong className="text-gray-900 dark:text-gray-100">{selectedRequest?.branch?.address || 'N/A'}</strong>
                            </div>
                            <div>
                                <span className="text-gray-500 block">Region:</span> 
                                <strong className="text-gray-900 dark:text-gray-100">{selectedRequest?.branch?.locationName || 'N/A'}</strong>
                            </div>
                            <div>
                                <span className="text-gray-500 block">Covered Area:</span> 
                                <span className="text-gray-700 dark:text-gray-300 font-semibold">{selectedRequest?.coveredArea ? `${selectedRequest.coveredArea} sq ft` : 'N/A'}</span>
                            </div>
                            <div>
                                <span className="text-gray-500 block">Request Date:</span> 
                                <span className="text-gray-700 dark:text-gray-300">{selectedRequest?.requestDate || 'N/A'}</span>
                            </div>
                            <div>
                                <span className="text-gray-500 block">Renovation Start:</span> 
                                <span className="text-gray-700 dark:text-gray-300">{selectedRequest?.renovationStartDate || 'N/A'}</span>
                            </div>
                            <div>
                                <span className="text-gray-500 block">Renovation Completion:</span> 
                                <span className="text-gray-700 dark:text-gray-300">{selectedRequest?.renovationCompletionDate || 'N/A'}</span>
                            </div>
                            {!isPSO && selectedRequest?.branch?.psoName && (
                                <div>
                                    <span className="text-gray-500 block">Assigned PSO:</span> 
                                    <strong className="text-gray-900 dark:text-gray-100">
                                        {selectedRequest.branch.psoName}
                                        {selectedRequest.branch.psoContact && selectedRequest.branch.psoContact !== 'N/A' ? ` (${selectedRequest.branch.psoContact})` : ''}
                                    </strong>
                                </div>
                            )}
                            {selectedRequest?.status === 'Approved' && (
                                <div>
                                    <span className="text-gray-500 block">In-charge Read Status:</span>
                                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold mt-1 ${
                                        selectedRequest.inchargeRead
                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                            : 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300 animate-pulse'
                                    }`}>
                                        <Icon icon={selectedRequest.inchargeRead ? FiCheckCircle : FiAlertCircle} className="h-3 w-3" />
                                        {selectedRequest.inchargeRead ? 'Read' : 'Unread'}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Selected Vendors & Regional Stats */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between border-b pb-1">
                            <h4 className="font-bold text-gray-800 dark:text-gray-200">Allocated Vendors</h4>
                            {loadingDetailShares && <span className="text-xs text-gray-500 italic">Calculating shares...</span>}
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            {/* CCTV */}
                            {(!isIncharge || isInchargeCCTV) && (
                                <div className="p-3 border rounded-lg bg-white dark:bg-gray-850">
                                    <span className="text-xs font-semibold text-gray-500">CCTV Vendor Selection</span>
                                    <p className="mt-1 font-bold text-gray-900 dark:text-gray-100">
                                        {selectedRequest?.cctvCompany?.companyName || 'None Selected'}
                                    </p>
                                    {detailCctvShare && selectedRequest?.branch && (
                                        <div className="mt-1.5 text-xs text-blue-600 dark:text-blue-400">
                                            Allocated: <strong>{detailCctvShare.allocatedCount} / {detailCctvShare.totalBranches} branches</strong> ({detailCctvShare.percentage}% share in {selectedRequest.branch.locationName})
                                        </div>
                                    )}
                                    {selectedRequest?.cctvKpiRemarks && (
                                        <div className="mt-2 border-t pt-1.5 text-xs text-gray-600 dark:text-gray-400">
                                            <strong>KPI Remarks:</strong> {selectedRequest.cctvKpiRemarks}
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* BAMS */}
                            {(!isIncharge || isInchargeBAMS) && (
                                <div className="p-3 border rounded-lg bg-white dark:bg-gray-850">
                                    <span className="text-xs font-semibold text-gray-500">BAMS Vendor Selection</span>
                                    <p className="mt-1 font-bold text-gray-900 dark:text-gray-100">
                                        {selectedRequest?.bamsCompany?.companyName || 'None Selected'}
                                    </p>
                                    {detailBamsShare && selectedRequest?.branch && (
                                        <div className="mt-1.5 text-xs text-blue-600 dark:text-blue-400">
                                            Allocated: <strong>{detailBamsShare.allocatedCount} / {detailBamsShare.totalBranches} branches</strong> ({detailBamsShare.percentage}% share in {selectedRequest.branch.locationName})
                                        </div>
                                    )}
                                    {selectedRequest?.bamsKpiRemarks && (
                                        <div className="mt-2 border-t pt-1.5 text-xs text-gray-600 dark:text-gray-400">
                                            <strong>KPI Remarks:</strong> {selectedRequest.bamsKpiRemarks}
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Guard */}
                            {(!isIncharge || isInchargeGuarding) && (
                                <div className="p-3 border rounded-lg bg-white dark:bg-gray-850">
                                    <span className="text-xs font-semibold text-gray-500">Guarding Vendor Selection</span>
                                    <p className="mt-1 font-bold text-gray-900 dark:text-gray-100">
                                        {selectedRequest?.guardCompany?.companyName || 'None Selected'}
                                    </p>
                                    {detailGuardShare && selectedRequest?.branch && (
                                        <div className="mt-1.5 text-xs text-blue-600 dark:text-blue-400">
                                            Allocated: <strong>{detailGuardShare.allocatedCount} / {detailGuardShare.totalBranches} branches</strong> ({detailGuardShare.percentage}% share in {selectedRequest.branch.locationName})
                                        </div>
                                    )}
                                    {selectedRequest?.guardKpiRemarks && (
                                        <div className="mt-2 border-t pt-1.5 text-xs text-gray-600 dark:text-gray-400">
                                            <strong>KPI Remarks:</strong> {selectedRequest.guardKpiRemarks}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* AI Remarks Suggestion Card */}
                    {(isRegionalHead || isHead) && selectedRequest?.status !== 'Approved' && (
                        <div className="flex gap-3 bg-purple-50 border border-purple-200 dark:bg-purple-950/20 dark:border-purple-900/30 p-4 rounded-xl text-purple-900 dark:text-purple-300">
                            <Icon icon={FiCpu} className="h-5 w-5 shrink-0 mt-0.5 text-purple-600 dark:text-purple-400" />
                            <div className="space-y-1">
                                <span className="text-xs font-bold uppercase tracking-wider text-purple-700 dark:text-purple-400 block">AI Security Recommendation</span>
                                <p className="text-xs font-medium leading-relaxed">{aiRemarksContent()}</p>
                            </div>
                        </div>
                    )}

                    {/* Comments / Justification & KPIs */}
                    <div className="space-y-1">
                        <h4 className="font-bold text-gray-800 dark:text-gray-200 font-semibold text-xs">Remarks</h4>
                        <p className="p-3 border rounded-xl bg-gray-50 dark:bg-gray-900/10 text-gray-700 dark:text-gray-300 min-h-[60px] whitespace-pre-wrap">
                            {selectedRequest?.reason || 'No details provided.'}
                        </p>
                    </div>

                    {/* Regional Head Remarks */}
                    {selectedRequest?.regionalHeadRemarks && (
                        <div className="space-y-1">
                            <h4 className="font-bold text-gray-800 dark:text-gray-200 font-semibold text-xs">Regional Head Security Remarks</h4>
                            <p className="p-3 border rounded-xl bg-purple-50/30 dark:bg-purple-950/10 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {selectedRequest.regionalHeadRemarks}
                            </p>
                        </div>
                    )}

                    {/* Head Security Remarks */}
                    {selectedRequest?.headSecurityRemarks && (
                        <div className="space-y-1">
                            <h4 className="font-bold text-gray-800 dark:text-gray-200 font-semibold text-xs">Head Security Review Remarks</h4>
                            <p className="p-3 border rounded-xl bg-green-50/30 dark:bg-green-950/10 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {selectedRequest.headSecurityRemarks}
                            </p>
                        </div>
                    )}

                    {/* Rejection Info (If Rejected) */}
                    {selectedRequest?.status === 'Rejected' && (
                        <div className="bg-red-50 dark:bg-red-950/20 p-4 border border-red-200 rounded-xl space-y-1 text-red-900 dark:text-red-200">
                            <h4 className="font-bold">Rejection Info ({selectedRequest.rejectionStage} stage)</h4>
                            <p><strong>Rejected By:</strong> {selectedRequest.rejectedByName || 'N/A'}</p>
                            <p><strong>Reason:</strong> {selectedRequest.rejectReason || 'No reason provided.'}</p>
                        </div>
                    )}

                    {/* Review textareas based on active role */}
                    {isRegionalHead && selectedRequest?.status === 'Pending' && (
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                Regional Head Security Remarks / Review *
                            </label>
                            <textarea
                                value={regionalRemarksInput}
                                onChange={(e) => setRegionalRemarksInput(e.target.value)}
                                placeholder="Add review notes for this allocation request before forwarding..."
                                rows={3}
                                className="w-full rounded-xl border border-gray-300 p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30"
                            />
                        </div>
                    )}

                    {isHead && selectedRequest?.status === 'Forwarded to Head' && (
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                Head Security Remarks / Review *
                            </label>
                            <textarea
                                value={headRemarksInput}
                                onChange={(e) => setHeadRemarksInput(e.target.value)}
                                placeholder="Add final approval/rejection remarks..."
                                rows={3}
                                className="w-full rounded-xl border border-gray-300 p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30"
                            />
                        </div>
                    )}

                    {/* Audit Trails */}
                    {(selectedRequest?.forwardedByName || selectedRequest?.approvedByName) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 text-xs text-gray-600 dark:text-gray-400">
                            {selectedRequest?.forwardedByName && (
                                <div>
                                    <strong>Forwarded By:</strong> {selectedRequest.forwardedByName}
                                    {selectedRequest.forwardedAt && <span className="block text-gray-500">On: {new Date(selectedRequest.forwardedAt.toDate ? selectedRequest.forwardedAt.toDate() : selectedRequest.forwardedAt).toLocaleString()}</span>}
                                </div>
                            )}
                            {selectedRequest?.approvedByName && (
                                <div>
                                    <strong>Approved By:</strong> {selectedRequest.approvedByName}
                                    {selectedRequest.approvedAt && <span className="block text-gray-500">On: {new Date(selectedRequest.approvedAt.toDate ? selectedRequest.approvedAt.toDate() : selectedRequest.approvedAt).toLocaleString()}</span>}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Action buttons inside View Details */}
                    <div className="flex justify-end gap-3 space-x-3 pt-6 border-t mt-6">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowViewModal(false);
                                setSelectedRequest(null);
                            }}
                            disabled={actionLoading}
                        >
                            Close
                        </Button>
                        
                        {/* Bank User actions inside View Details */}
                        {isBankUser && selectedRequest && (
                            <>
                                <Button
                                    onClick={() => {
                                        setShowViewModal(false);
                                        openEditModal(selectedRequest);
                                    }}
                                    disabled={actionLoading}
                                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5"
                                >
                                    <Icon icon={FiEdit} className="h-4 w-4" />
                                    Edit Request
                                </Button>
                                <Button
                                    onClick={() => {
                                        setShowViewModal(false);
                                        handleDeleteClick(selectedRequest);
                                    }}
                                    disabled={actionLoading}
                                    className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-1.5"
                                >
                                    <Icon icon={FiTrash2} className="h-4 w-4" />
                                    Delete Request
                                </Button>
                            </>
                        )}

                        {/* Resubmit options for PSO and Regional Heads */}
                        {(selectedRequest?.status === 'Rejected' || selectedRequest?.status === 'Cancelled') && 
                         (isPSO || isRegionalHead) && (
                            <Button
                                onClick={() => {
                                    setShowViewModal(false);
                                    openEditModal(selectedRequest);
                                }}
                                disabled={actionLoading}
                                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5"
                            >
                                <Icon icon={FiEdit} className="h-4 w-4" />
                                Edit & Resubmit
                            </Button>
                        )}
                        
                        {/* Regional Review Actions */}
                        {isRegionalHead && selectedRequest?.status === 'Pending' && (
                            <>
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setShowViewModal(false);
                                        openEditModal(selectedRequest);
                                    }}
                                    disabled={actionLoading}
                                    className="flex items-center gap-1 hover:border-blue-500 hover:text-blue-600"
                                >
                                    <Icon icon={FiEdit} className="h-4 w-4" />
                                    Edit Vendors
                                </Button>
                                <Button
                                    onClick={handleForwardToHead}
                                    disabled={actionLoading}
                                    className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1.5"
                                >
                                    {actionLoading && <Icon icon={FiLoader} className="h-4 w-4 animate-spin" />}
                                    {actionLoading ? 'Forwarding...' : 'Forward to Head'}
                                </Button>
                                <Button
                                    onClick={handleRejectClick}
                                    disabled={actionLoading}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                    Reject Request
                                </Button>
                            </>
                        )}

                        {/* Head Review Actions */}
                        {isHead && selectedRequest?.status === 'Forwarded to Head' && (
                            <>
                                <Button
                                    onClick={handleApprove}
                                    disabled={actionLoading}
                                    className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-1.5"
                                >
                                    {actionLoading && <Icon icon={FiLoader} className="h-4 w-4 animate-spin" />}
                                    {actionLoading ? 'Approving...' : 'Approve Request'}
                                </Button>
                                <Button
                                    onClick={handleRejectClick}
                                    disabled={actionLoading}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                    Reject Request
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Toast alerts */}
            {toast.show && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToast({ ...toast, show: false })}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
